import * as joda from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { checkAllCasesHandled } from "trader.ts/utils";
import { match } from "ts-pattern";
import * as routes from "../classyts/routes";
import {
  enrollment_status,
  formulachargemode,
  lessonid,
  messagetemplatetype,
  planningmode,
  questiontype,
} from "../sql/classy/types";
import * as FA from "./../styles/fontawesome.css.classes";
import * as style from "./../styles/style.css.classes";
import {
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "./domain/config";
import { invoiceSubject } from "./domain/finance/types";
import { capitalize } from "./lib/capitalize";
import { bluePlanningColors, colors, green } from "./lib/colors";
import { currencyToNumber } from "./lib/currency";
import { isStudentActiveAtLesson } from "./lib/isstudentactiveatlesson";
import { isNil } from "./lib/istype";
import {
  enrollmentRequestStatus,
  formulaType,
  invoicelinetype,
  lessonRemovedReason,
  parseWeekday,
} from "./parsing_from_ur";
import {
  describePeriod,
  showWeekday,
  translateMonth,
  translateWeekday,
} from "./translations_specific";

export function renderFirstNameLastName(s: {
  firstname: string;
  lastname: string;
}): string {
  return capitalize(s.firstname.toLowerCase()) + " " + s.lastname;
}

export const HHmm = joda.DateTimeFormatter.ofPattern("HH:mm");
export const ddMM = joda.DateTimeFormatter.ofPattern("dd/MM");
export const ddMMyyyy = joda.DateTimeFormatter.ofPattern("dd/MM/yyyy");
export function ddMMMMyyyy(d: joda.LocalDate): string {
  return d.dayOfMonth() + " " + translateMonth(d.month()) + " " + d.year();
}
export function ddMMMM(d: joda.LocalDate): string {
  return d.dayOfMonth() + " " + translateMonth(d.month());
}
export function ddMMM(d: joda.LocalDate): string {
  return d.dayOfMonth() + " " + translateMonth(d.month()).slice(0, 3);
}
export function ddMMMyyyy(d: joda.LocalDate): string {
  return (
    d.dayOfMonth() +
    " " +
    translateMonth(d.month()).slice(0, 3) +
    " " +
    d.year()
  );
}

export function renderTimeRange(t: joda.LocalTime, duration: number) {
  return t.format(HHmm) + " - " + t.plusMinutes(duration).format(HHmm);
}

export function renderLessonLocation(
  lesson_location: "NotSpecifiedS" | "OnlineS" | "HomeS" | "RoomS",
  room_description: string | null,
): string {
  return match(lesson_location)
    .with("NotSpecifiedS", () => "Geen locatie")
    .with("OnlineS", () => "Online")
    .with("HomeS", () => "Thuis")
    .with("RoomS", () => room_description || "")
    .exhaustive();
}

export function renderPlanningMode(pm: planningmode): string {
  return match(pm)
    .with("Weekly", () => "Wekelijks")
    .with("Biweekly", () => "Tweewekelijks")
    .with("Manual", () => "Manueel ingepland")
    .with("Flex", () => "Beurtenkaart")
    .exhaustive();
}

export function showInvoicelineType(n: invoicelinetype): string {
  return match(n)
    .with("Normal", () => "Te betalen")
    .with("Discount", () => "Korting")
    .with("Optional", () => "Optioneel")
    .exhaustive();
}

export function showFormulaChargeMode(n: formulachargemode): string {
  return match(n)
    .with("Enrollment", () => "Vast")
    .with("Lesson", () => "1x per les")
    .exhaustive();
}

export function renderInvoiceSubjectAsTextForStudent(
  subject: invoiceSubject,
  opts: { showStudent: boolean },
): string {
  if (subject.tag === "enrollment") {
    const enr = subject;
    return capitalize(
      (opts.showStudent ? renderFirstNameLastName(enr.student) + " " : "") +
        "bij %teachername, %instrumentname op %weekday om %time"
          .replace("%teachername", renderFirstNameLastName(enr.teacher))
          .replace("%instrumentname", enr.instrument.description)
          .replace("%weekday", translateWeekday(parseWeekday(enr.lg.weekday)))
          .replace("%time", enr.lg.time.format(HHmm)),
    );
  } else if (subject.tag === "enrollmentrequest") {
    return capitalize(
      (opts.showStudent ? renderFirstNameLastName(subject.student) + " " : "") +
        "aanvraag: %teacher"
          .replace(
            "%formuladescr",
            subject.enr_req.contents.formula.description,
          )
          .replace(
            "%teacher",
            subject.enr_req.contents.teacher === null
              ? subject.enr_req.contents.instrument.description
              : renderFirstNameLastName(subject.enr_req.contents.teacher),
          ),
    );
  } else if (subject.tag === "periodfee") {
    return (
      "Jaargeld" +
      " " +
      (opts.showStudent ? renderFirstNameLastName(subject.student) + " " : "") +
      describePeriod(subject.period.firstday, subject.period.lastday)
    );
  } else if (subject.tag === "student_charge") {
    return (
      (opts.showStudent ? renderFirstNameLastName(subject.student) + " " : "") +
      subject.student_charge.description
    );
  } else {
    return checkAllCasesHandled(subject);
  }
}

export function renderEnrollmentrequestStatus(
  r: enrollmentRequestStatus,
): [string, colors] {
  switch (r) {
    case "InvitationER":
      return ["Uitnodiging aangemaakt", green];
    case "InvitationRejectedER":
      return ["Uitnodiging aangemaakt, afgewezen", bluePlanningColors];
    case "Reserved":
      return ["Reservatie aangemaakt", green];
    case "WaitingForPaymentER":
      return ["Inschrijvingsaanvraag wacht op betaling", green];
    case "WaitingForPaymentAtProviderER":
      return ["Inschrijvingsaanvraag wacht op betaling", green];
    case "WaitingForApprovalER":
      return ["Inschrijvingsaanvraag wacht op goedkeuring", green];
    case "DisprovedER":
      return ["Inschrijvingsaanvraag afgekeurd", bluePlanningColors];
    case "WaitingForEmailConfirmationTimedOutER":
      return ["Inschrijvingsaanvraag verlopen", bluePlanningColors];
    case "WaitingForPaymentTimedOutER":
      return ["Inschrijvingsaanvraag verlopen", bluePlanningColors];
    case "WaitingForPaymentAtProviderTimedOutER":
      return ["Inschrijvingsaanvraag verlopen", bluePlanningColors];
    case "ConvertedToEnrollment":
      return ["Inschrijvingsaanvraag goedgekeurd", green];
    case "WaitingForEmailConfirmationER":
      return [
        "Inschrijvingsaanvraag wacht op bevestiging emailadres",
        green,
      ];
    default:
      return checkAllCasesHandled(r);
  }
}

export function renderTeacherPlusTimePlusWeekday(
  teach: { firstname: string; lastname: string },
  t: joda.LocalTime,
  wd: joda.DayOfWeek,
): string {
  return "Bij %teacher op %weekday om %time"
    .replace("%teacher", renderFirstNameLastName(teach))
    .replace("%time", t.format(HHmm))
    .replace("%weekday", showWeekday(wd));
}

export function renderTeacherPlusTimePlusWeekdayPlusDaterange(
  teach: { firstname: string; lastname: string },
  t: joda.LocalTime,
  wd: joda.DayOfWeek,
  days: {
    firstlesson: joda.LocalDate | null;
    lastlesson: joda.LocalDate | null;
  },
): string {
  return (
    renderTeacherPlusTimePlusWeekday(teach, t, wd) +
    (days.firstlesson && days.lastlesson
      ? " " +
        "van %firstlesson tot %lastlesson"
          .replace("%firstlesson", days.firstlesson.format(ddMMyyyy))
          .replace("%lastlesson", days.lastlesson.format(ddMMyyyy))
      : "")
  );
}

export function renderTimePlusWeekday(
  t: joda.LocalTime,
  wd: joda.DayOfWeek,
): string {
  return "%weekday om %time"
    .replace("%time", t.format(HHmm))
    .replace("%weekday", showWeekday(wd));
}

export function renderTimeRangePlusWeekday(
  starttime: joda.LocalTime,
  endtime: joda.LocalTime,
  wd: joda.DayOfWeek,
): string {
  return "%weekday van %starttime tot %endttime"
    .replace("%starttime", starttime.format(HHmm))
    .replace("%endtime", endtime.format(HHmm))
    .replace("%weekday", showWeekday(wd));
}

export function renderLessonRemovedReason(rr: lessonRemovedReason): string {
  switch (rr) {
    case "MovedDueToTeacherAbsence":
      return "Inhaalles gepland";
    case "DeletedDueToTeacherAbsence":
      return "Les afgelast";
    case "DeletedManually":
      return "Les verwijderd";
    case "RemovedBecauseOfNewHoliday":
      return "Les verwijderd door nieuwe vakantieperiode";
    case "RemovedBecauseOfRemovalOfHoliday":
      return "Les verwijderd door heropening vakantieperiode";
    case "StudentLegalAbsence":
      return "Student afwezig met vervangles";
  }
}

export function renderFormulaType(ft: formulaType): string {
  if (ft.tag === "Private") {
    return "Privéles";
  } else if (ft.tag === "Group") {
    return "Groepsles - max %n".replace(
      "%n",
      ft.maxnumberofstudents.toString(),
    );
  } else if (ft.tag === "Camp") {
    return "Kamp - max %n".replace(
      "%n",
      ft.maxnumberofstudentscamp.toString(),
    );
  } else {
    return checkAllCasesHandled(ft);
  }
}

export function renderFormulaTypeShort(ft: {
  tag: formulaType["tag"];
}): string {
  if (ft.tag === "Private") {
    return "Privéles";
  } else if (ft.tag === "Group") {
    return "Groepsles";
  } else if (ft.tag === "Camp") {
    return "Kamp";
  } else {
    return checkAllCasesHandled(ft.tag);
  }
}

export function renderHeaderItemWithMusicNote(title: string, content: string) {
  return (
    <article className={style.text_with_icon}>
      <i
        style="padding-top: 1px; height: 24px; width: 24px; font-size: 0.8rem;"
        className={FA.icon_music_solid + style.icon_circle}
      ></i>
      <div>
        <div style="font-size: 0.85rem; margin-bottom: -8px;">{title}</div>
        <div style="font-size: 1.0rem; font-weight: 600;">{content}</div>
      </div>
    </article>
  );
}

export function wrapInParentheses(s: string): string {
  return "(" + s + ")";
}

export function wrapInParenthesesIfNotEmpty(s: string): string {
  return s.length > 0 ? "(" + s + ")" : "";
}

export function renderAnswer(question: {
  label: string;
  text_answer: string | null;
  number_answer: number | null;
  boolean_answer: boolean | null;
  select_answer: string | null;
  multiselect_answers: string[] | null;
  questiontype: questiontype;
}) {
  return question.questiontype === "boolean"
    ? question.boolean_answer === true
      ? "Ja"
      : question.boolean_answer === false
      ? "Nee"
      : "_"
    : question.questiontype === "text"
    ? question.text_answer || "_"
    : question.questiontype === "number"
    ? !isNil(question.number_answer)
      ? question.number_answer?.toString()
      : "_"
    : question.questiontype === "select"
    ? question.select_answer || "_"
    : question.questiontype === "multiselect"
    ? question.multiselect_answers?.join(", ") || "_"
    : checkAllCasesHandled(question.questiontype);
}

export function renderPaymentScheme(
  s:
    | Omit<periodic_payment_scheme, "id">
    | Omit<non_periodic_payment_scheme, "id">,
): string {
  if (s.tag === "periodic") {
    return s.label;
  } else if (s.tag === "non_periodic") {
    return s.label;
  } else {
    return checkAllCasesHandled(s);
  }
}

export function renderMessageTemplateType(t: messagetemplatetype) {
  return match(t)
    .with("EnrollmentCreatedPrivate", () =>
      "Nieuwe inschrijving - Privéles",
    )
    .with("EnrollmentCreatedGroup", () =>
      "Nieuwe inschrijving - Groepsles",
    )
    .with("EnrollmentCreatedCamp", () => "Nieuwe inschrijving - Kamp")
    .with("EvaluationFinalized", () => "Nieuwe evaluatie")
    .with("InvoiceReminder", () => "Nog niet betaald - herinnering")
    .with("OtherInvoiceReminder", () =>
      "Herinnering betaling andere facturen",
    )
    .with("PeriodicPaymentReminder", () =>
      "Herinnering periodieke betaling",
    )
    .with("StudentChargeCreated", () => "Losse factuur aangemaakt")
    .with("DirectDebitAnnouncement", () => "Aankondiging incasso")
    .with("TeacherAbsence_Replacement", () =>
      "Docent afwezig, vervanging",
    )
    .with("TeacherAbsence_Move", () =>
      "Docent afwezig, les verplaatst",
    )
    .with("TeacherAbsence_Removed", () =>
      "Docent afwezig, les verwijderd",
    )
    .with("TeacherAbsence_Nothing", () => "Docent afwezig, geen actie")
    .with("TeacherAbsence_Replacement_SMS", () =>
      "Docent afwezig, vervanging - SMS",
    )
    .with("TeacherAbsence_Move_SMS", () =>
      "Docent afwezig, les verplaatst - SMS",
    )
    .with("TeacherAbsence_Removed_SMS", () =>
      "Docent afwezig, les verwijderd - SMS",
    )
    .with("TeacherAbsence_Nothing_SMS", () =>
      "Docent afwezig, geen actie - SMS",
    )
    .with("InvitationCreated", () =>
      "Nieuwe herinschrijving voorgesteld",
    )
    .with("MutualityCertificateCreated", () =>
      "Mutualiteitsattest aangemaakt",
    )
    .with("NewMessage", () => "Nieuw bericht")
    .with("NewTextNote", () => "Nieuwe notitie")
    .with("NewFileNote", () => "Nieuw bestand geupload bij les")
    .exhaustive();
}

export function renderedInvoiceSubjectToHtml(r: {
  description: string;
  link: string | null;
}): HTMLElement {
  return r.link === null ? (
    <span>{r.description}</span>
  ) : (
    <a href={r.link}>{r.description}</a>
  );
}

export function renderInvoiceSubjectForOverviews(subject: invoiceSubject): {
  description: string;
  link: string | null;
} {
  if (subject.tag === "enrollment") {
    return {
      description: "%teacher - %weekday - %time"
        .replace("%weekday", translateWeekday(parseWeekday(subject.lg.weekday)))
        .replace("%time", subject.lg.time.format(HHmm))
        .replace(
          "%teacher",
          renderFirstNameLastName({
            firstname: subject.teacher.firstname,
            lastname: subject.teacher.lastname,
          }),
        ),
      link: routes.lessongroup_generalRoute.link({
        lessongroupid: subject.lg.id,
      }),
    };
  } else if (subject.tag === "enrollmentrequest") {
    return {
      description: "Aanvraag: %teacher"
        .replace("%formuladescr", subject.enr_req.contents.formula.description)
        .replace(
          "%teacher",
          subject.enr_req.contents.teacher === null
            ? subject.enr_req.contents.instrument.description
            : renderFirstNameLastName(subject.enr_req.contents.teacher),
        ),
      link: routes.enrollment_requestRoute.link({
        enrollmentrequestid: subject.enr_req.id,
      }),
    };
  } else if (subject.tag === "periodfee") {
    return {
      description:
        "Jaargeld" +
        " " +
        describePeriod(subject.period.firstday, subject.period.lastday),
      link: null,
    };
  } else if (subject.tag === "student_charge") {
    return {
      description: subject.student_charge.description,
      link: null,
    };
  } else {
    return checkAllCasesHandled(subject);
  }
}

export function renderInvoiceSubjectTitle(s: invoiceSubject): string {
  return match(s)
    .with({ tag: "enrollment" }, (e) => "Inschrijving")
    .with({ tag: "enrollmentrequest" }, () => "Aanvraag")
    .with({ tag: "periodfee" }, () => "Jaargeld")
    .with({ tag: "student_charge" }, () => "Losse factuur")
    .exhaustive();
}

export function renderInvoiceSubjectTextWithFormula(s: invoiceSubject): string {
  const rendered = renderInvoiceSubjectForOverviews(s);
  return (
    match(s)
      .with({ tag: "enrollment" }, (e) => e.lg.formula.description + " - ")
      .with({ tag: "enrollmentrequest" }, () => "")
      .with({ tag: "periodfee" }, () => "")
      .with({ tag: "student_charge" }, () => "")
      .exhaustive() + rendered.description
  );
}

export function renderInvoiceSubjectForStudentEmail(
  s: invoiceSubject,
  opts?: { listStyle: string },
): {
  title: string;
  list: HTMLElement;
} {
  return match(s)
    .with({ tag: "enrollment" }, (e) => ({
      title: "Je inschrijving",
      list: (
        <ul style={opts?.listStyle || ""}>
          <li>{e.lg.formula.description}</li>
          <li>
            {"Bij %teacher".replace(
              "%teacher",
              renderFirstNameLastName(e.teacher),
            )}
          </li>
          <li>
            {renderTimePlusWeekday(e.lg.time, parseWeekday(e.lg.weekday))}
          </li>
        </ul>
      ),
    }))
    .with({ tag: "enrollmentrequest" }, (e) => ({
      title: "Je inschrijving",
      list: (
        <ul style={opts?.listStyle || ""}>
          <li>{e.enr_req.contents.formula.description}</li>
          <li>
            {"Bij %teacher".replace(
              "%teacher",
              e.enr_req.contents.teacher === null
                ? e.enr_req.contents.instrument.description
                : renderFirstNameLastName(e.enr_req.contents.teacher),
            )}
          </li>
        </ul>
      ),
    }))
    .with({ tag: "periodfee" }, (e) => ({
      title: "Je jaargeld",
      list: (
        <ul style={opts?.listStyle || ""}>
          <li>{describePeriod(e.period.firstday, e.period.lastday)}</li>
        </ul>
      ),
    }))
    .with({ tag: "student_charge" }, (e) => ({
      title: "Je factuur",
      list: (
        <ul style={opts?.listStyle || ""}>
          <li>{e.student_charge.description}</li>
        </ul>
      ),
    }))
    .exhaustive();
}

export function getEnrollmentLastLesson(enr: {
  lessons: { id: lessonid; date: joda.LocalDate }[];
  startedlessonson: joda.LocalDate;
  stoppedlessonson: joda.LocalDate | null;
}): joda.LocalDate | null {
  return (
    enr.lessons
      .filter((l) =>
        isStudentActiveAtLesson(l.date, {
          enrollment_startedlessonson: enr.startedlessonson,
          enrollment_stoppedlessonson: enr.stoppedlessonson,
        }),
      )
      .maxBy((l) => l.date.toEpochDay())?.date || null
  );
}

export function renderEnrollmentStatus(
  status: enrollment_status,
  enr: {
    lessons:
      | {
          tag: "show_last_lesson";
          lessons: { id: lessonid; date: joda.LocalDate }[];
        }
      | { tag: "dont_show_last_lesson" };
    startedlessonson: joda.LocalDate;
    stoppedlessonson: joda.LocalDate | null;
  },
): string {
  return status === "Stopped"
    ? enr.lessons.tag === "show_last_lesson"
      ? `Gestopt, laatste les op %date`.replace(
          "%date",
          getEnrollmentLastLesson({
            ...enr,
            lessons: enr.lessons.lessons,
          })?.format(ddMMyyyy) || "",
        )
      : "Gestopt"
    : status === "TrialLesson"
    ? `Proefles op %date`.replace(
        "%date",
        enr.startedlessonson.format(ddMMyyyy),
      )
    : status === "ActiveEnrollment"
    ? ""
    : checkAllCasesHandled(status);
}

export function renderCurrency(c: currency) {
  return "€ " + currencyToNumber(c).toString();
}
